import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Toaster from '../components/Toaster';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import StatusBar from '../components/StatusBar';
import { Sidebar } from '../components/Sidebar';
import FreezeBanner from '../components/FreezeBanner';
import { LoginModal } from '../components/LoginModal';
import { hasFirebaseConfig } from '../lib/firebase';
import { TenantBanner } from '../components/TenantBanner';
// import { DevBanner } from '../components/DevBanner';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { isTokenValid } from '../lib/auth';
import { useAppStore } from '../store/appStore';
import { ServiceHealthProvider } from '../lib/ServiceHealthContext';
// Import dev auth for automatic authentication in development
import '../lib/devAuth';

export function App() {
  const { i18n } = useTranslation();
  const { token, isAuthenticated, hydrated, signOut } = useAuthStore();
  const { language, setShowLoginModal } = useUIStore();
  const { appId } = useAppStore();
  const loc = useLocation();

  // On dev.devcockpit.ai (and the raw droplet IP), backend services use a dev-bypass.
  // The UI should remain usable even if no JWT has been injected into localStorage.
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isTestServer = hostname === '159.65.26.108';
  const isDevDomain = hostname === 'dev.devcockpit.ai';
  const allowAnonymousDev = (isDevDomain || isTestServer) && !hasFirebaseConfig();

  // DEBUG LOGGING
  useEffect(() => {
    const enabled = (() => {
      try {
        return localStorage.getItem('dc.debug.app') === '1';
      } catch {
        return false;
      }
    })();
    if (!enabled) return;
    console.log('🚀 App.tsx: Component mounted');
    console.log('🔐 Auth state:', {
      hasToken: !!token,
      isAuthenticated,
      tokenLength: token?.length || 0,
      hostname: window.location.hostname
    });
  }, []);

  useEffect(() => {
    const enabled = (() => {
      try {
        return localStorage.getItem('dc.debug.app') === '1';
      } catch {
        return false;
      }
    })();
    if (!enabled) return;
    console.log('🔄 Auth state changed:', {
      hasToken: !!token,
      isAuthenticated,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });
  }, [token, isAuthenticated]);

  // Sync i18n language with store
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  // Check token validity on mount and periodically
  useEffect(() => {
    if (token && !isTokenValid(token)) {
      signOut();
    }

    // Check token validity every minute
    const interval = setInterval(() => {
      if (token && !isTokenValid(token)) {
        signOut();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [token, signOut]);

  // Show login modal if not authenticated (unless we explicitly allow anonymous dev)
  useEffect(() => {
    if (!hydrated) return;

    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDev = import.meta.env.DEV || isDevDomain || isLocalhost || isTestServer;

    if (!isAuthenticated && !isDev && !allowAnonymousDev) {
      setShowLoginModal(true);
    } else if (!isAuthenticated && isDev && !allowAnonymousDev) {
      // In development, give the auto-auth a moment to work before showing modal
      const timer = setTimeout(() => {
        if (!useAuthStore.getState().isAuthenticated) {
          const enabled = (() => {
            try {
              return localStorage.getItem('dc.debug.app') === '1';
            } catch {
              return false;
            }
          })();
          if (enabled) console.log('⚠️ Dev auto-auth failed, showing login modal as fallback');
          setShowLoginModal(true);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [hydrated, isAuthenticated, setShowLoginModal, allowAnonymousDev]);

  const effectiveAppId = appId && appId !== 'farmer-portal' ? appId : null;

  if (!hydrated) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-600">
          <div className="text-lg font-medium text-gray-900 mb-2">Loading session...</div>
          <div className="text-sm">Restoring authentication state</div>
        </div>
      </div>
    );
  }

  return (
    <ServiceHealthProvider>
    <div className="h-screen flex flex-col">
      <Toaster />
      <Header />
      <StatusBar />
      {/* <DevBanner /> */}
      <FreezeBanner />
      <TenantBanner />
      
      <div className="flex flex-1 overflow-hidden min-h-0 min-w-0">
        <Sidebar />
        
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-gray-50">
          {isAuthenticated || allowAnonymousDev ? (
            // Hard requirement: an app instance must be selected for all app-scoped screens.
            // Allow /apps (to select/create) and /system (global health) without a selection.
            (!effectiveAppId && !loc.pathname.startsWith('/apps') && !loc.pathname.startsWith('/system') && !loc.pathname.startsWith('/agent-actions')) ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-xl px-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Select an App Instance
                  </h2>
                  <p className="text-gray-600 mb-4">
                    This control plane is app-scoped. Pick an app instance first so all views stay isolated.
                  </p>
                  <a href="/apps" className="btn-primary inline-block px-4 py-2">
                    Go to Apps
                  </a>
                </div>
              </div>
            ) : (
              <Outlet />
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Authentication Required
                </h2>
                <p className="text-gray-600">
                  Please sign in to access the DevCockpit Farmer Portal
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      <LoginModal />
    </div>
    </ServiceHealthProvider>
  );
}
// Trigger automated deployment
